/* Femme Botanicals — home page behaviours.
   Everything degrades gracefully: content is visible without JS. */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- 1. Generic reveal-on-scroll ---------- */
  function showReveal(el) {
    var delay = parseFloat(el.dataset.revealDelay || 0);
    if (delay) {
      setTimeout(function () { el.classList.add('is-in'); }, delay);
    } else {
      el.classList.add('is-in');
    }
  }

  function initReveal() {
    var items = [].slice.call(document.querySelectorAll('.fb-reveal'));
    if (!items.length) return;

    if (reduced || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        showReveal(entry.target);
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0 });

    items.forEach(function (el) { io.observe(el); });

    // Safety sweep. A fast scroll, an anchor jump or restored scroll position can
    // move an element from below the fold to above it between two sampled frames;
    // IntersectionObserver never reports an intersection and the content would
    // stay invisible for good. Anything at or past the fold is revealed here.
    function sweep() {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      for (var i = items.length - 1; i >= 0; i--) {
        var el = items[i];
        if (el.classList.contains('is-in')) { items.splice(i, 1); continue; }
        if (el.getBoundingClientRect().top < vh * 0.92) {
          showReveal(el);
          io.unobserve(el);
          items.splice(i, 1);
        }
      }
    }

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () { sweep(); ticking = false; });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    window.addEventListener('load', onScroll);
    sweep();
  }

  /* ---------- 2. Word-by-word text reveal on scroll ---------- */
  function initWordReveal() {
    var blocks = document.querySelectorAll('[data-fb-word-reveal]');
    if (!blocks.length) return;

    blocks.forEach(function (block) {
      // Wrap each word in a span, preserving inline markup one level deep.
      if (!block.dataset.fbWordsReady) {
        wrapWords(block);
        block.dataset.fbWordsReady = 'true';
      }
      if (reduced) {
        block.querySelectorAll('.fb-word').forEach(function (w) { w.style.opacity = 1; });
        return;
      }
      block.classList.add('fb-words-active');
    });

    if (reduced) return;

    var onScroll = function () {
      blocks.forEach(function (block) {
        var rect = block.getBoundingClientRect();
        var vh = window.innerHeight || document.documentElement.clientHeight;
        // Progress runs from "block top hits 78% of viewport" to "block bottom hits 28%".
        var start = vh * 0.78;
        var end = vh * 0.28;
        var total = (rect.height + (start - end)) || 1;
        var travelled = start - rect.top;
        var progress = Math.min(1, Math.max(0, travelled / total));

        var words = block.querySelectorAll('.fb-word');
        var count = words.length;
        var lit = progress * count;
        for (var i = 0; i < count; i++) {
          var o = Math.min(1, Math.max(0, lit - i));
          words[i].style.opacity = (0.22 + o * 0.78).toFixed(3);
        }
      });
    };

    var ticking = false;
    var raf = function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () { onScroll(); ticking = false; });
    };
    window.addEventListener('scroll', raf, { passive: true });
    window.addEventListener('resize', raf, { passive: true });
    onScroll();
  }

  function wrapWords(root) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    var textNodes = [];
    var n;
    while ((n = walker.nextNode())) {
      if (n.nodeValue.trim()) textNodes.push(n);
    }
    textNodes.forEach(function (node) {
      var frag = document.createDocumentFragment();
      node.nodeValue.split(/(\s+)/).forEach(function (chunk) {
        if (!chunk) return;
        if (/^\s+$/.test(chunk)) {
          frag.appendChild(document.createTextNode(chunk));
        } else {
          var s = document.createElement('span');
          s.className = 'fb-word';
          s.textContent = chunk;
          frag.appendChild(s);
        }
      });
      node.parentNode.replaceChild(frag, node);
    });
  }

  /* ---------- 3. Horizontal rails (product cards, testimonials) ---------- */
  function initRails() {
    document.querySelectorAll('[data-fb-rail]').forEach(function (rail) {
      var track = rail.querySelector('[data-fb-rail-track]');
      if (!track) return;
      var prev = rail.querySelector('[data-fb-rail-prev]');
      var next = rail.querySelector('[data-fb-rail-next]');

      var step = function () {
        var card = track.querySelector('[data-fb-rail-item]');
        if (!card) return track.clientWidth * 0.8;
        var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || 0) || 0;
        return card.getBoundingClientRect().width + gap;
      };

      if (prev) prev.addEventListener('click', function () {
        track.scrollBy({ left: -step(), behavior: reduced ? 'auto' : 'smooth' });
      });
      if (next) next.addEventListener('click', function () {
        track.scrollBy({ left: step(), behavior: reduced ? 'auto' : 'smooth' });
      });

      var sync = function () {
        var max = track.scrollWidth - track.clientWidth - 1;
        if (prev) prev.disabled = track.scrollLeft <= 1;
        if (next) next.disabled = track.scrollLeft >= max;
      };
      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync, { passive: true });
      sync();

      /* Pointer drag for desktop */
      var down = false, startX = 0, startScroll = 0, moved = false;
      track.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'touch') return;
        down = true; moved = false;
        startX = e.clientX;
        startScroll = track.scrollLeft;
        track.classList.add('is-grabbing');
      });
      window.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        if (Math.abs(dx) > 4) moved = true;
        track.scrollLeft = startScroll - dx;
      });
      window.addEventListener('pointerup', function () {
        if (!down) return;
        down = false;
        track.classList.remove('is-grabbing');
      });
      track.addEventListener('click', function (e) {
        if (moved) { e.preventDefault(); e.stopPropagation(); }
      }, true);
    });
  }

  /* ---------- 4. Seamless marquees ---------- */
  function initMarquees() {
    document.querySelectorAll('[data-fb-marquee]').forEach(function (el) {
      var track = el.querySelector('[data-fb-marquee-track]');
      if (!track || track.dataset.fbCloned) return;
      // Duplicate content until it comfortably overflows twice the viewport.
      var original = track.innerHTML;
      var guard = 0;
      while (track.scrollWidth < el.clientWidth * 2 && guard < 12) {
        track.insertAdjacentHTML('beforeend', original);
        guard++;
      }
      track.dataset.fbCloned = 'true';
      var speed = parseFloat(el.dataset.fbMarqueeSpeed || 60); // px per second
      var distance = track.scrollWidth / 2;
      if (distance > 0) {
        track.style.setProperty('--fb-marquee-distance', distance + 'px');
        track.style.setProperty('--fb-marquee-duration', (distance / speed) + 's');
      }
    });
  }

  /* ---------- 5. Judge.me testimonials as a card rail ----------
     The app fades one card at a time; the design shows the cards side by
     side (laid out in fb-home.css), so its arrows scroll the row instead. */
  function initReviewRail() {
    document.querySelectorAll('.jdgm-testimonials-carousel').forEach(function (widget) {
      var track = widget.querySelector('.jdgm-testimonials-container');
      if (!track || widget.dataset.fbRail) return;
      widget.dataset.fbRail = 'true';

      // The app marks every card but the current one inert and aria-hidden.
      var expose = function () {
        track.querySelectorAll('.jdgm-carousel-item').forEach(function (card) {
          card.removeAttribute('inert');
          card.removeAttribute('aria-hidden');
        });
      };
      expose();
      new MutationObserver(expose).observe(track, {
        subtree: true, attributes: true, attributeFilter: ['inert', 'aria-hidden']
      });

      widget.querySelectorAll('.jdgm-arrow').forEach(function (btn, i) {
        btn.removeAttribute('onclick');
        btn.onclick = null;
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopImmediatePropagation();
          var card = track.querySelector('.jdgm-carousel-item');
          var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
          var step = card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
          track.scrollBy({ left: i === 0 ? -step : step, behavior: reduced ? 'auto' : 'smooth' });
        }, true);
      });
    });
  }

  function boot() {
    initReveal();
    initWordReveal();
    initRails();
    initMarquees();
    initReviewRail();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* The review widget can finish rendering after DOMContentLoaded. */
  window.addEventListener('load', initReviewRail);

  /* Re-run inside the Shopify theme editor when a section is re-rendered. */
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:select', boot);
})();
